import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Dialog for configuring feature settings that support children.
 * Shows:
 * - Default for new children toggle
 * - Bulk enable/disable actions
 */
function FeatureSettingsDialog({
  open,
  onClose,
  association,
  entityType,
  entityId,
  onUpdateFeature,
  onBulkAction,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [defaultEnabled, setDefaultEnabled] = useState(association?.default_enabled_for_children ?? false);
  const [loading, setLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Sync state when feature changes
  useEffect(() => {
    setDefaultEnabled(association?.default_enabled_for_children ?? false);
  }, [association?.default_enabled_for_children]);

  // Reset bulk result when dialog opens
  useEffect(() => {
    if (open) {
      setBulkResult(null);
    }
  }, [open]);

  const childEntityLabel =
    entityType === 'customer' ? t('features.siteGroups', 'site groups') : t('features.sites', 'sites');
  const childEntityLabelSingular =
    entityType === 'customer' ? t('features.siteGroup', 'site group') : t('features.site', 'site');

  const handleDefaultEnabledChange = async event => {
    const newValue = event.target.checked;
    setLoading(true);
    try {
      await onUpdateFeature({
        id: association.id,
        default_enabled_for_children: newValue,
      });
      setDefaultEnabled(newValue);
    } catch (error) {
      console.error('Failed to update default_enabled_for_children:', error);
    }
    setLoading(false);
  };

  const handleBulkEnable = async () => {
    setLoading(true);
    setBulkResult(null);
    try {
      const result = await onBulkAction({
        featureKey: association.feature.key,
        entityType,
        entityId,
        action: 'enable_children',
      });
      if (result?.payload) {
        setBulkResult(result.payload);
      }
    } catch (error) {
      console.error('Failed to enable all children:', error);
    }
    setLoading(false);
  };

  const handleBulkDisable = async () => {
    setLoading(true);
    setBulkResult(null);
    try {
      const result = await onBulkAction({
        featureKey: association.feature.key,
        entityType,
        entityId,
        action: 'disable_children',
      });
      if (result?.payload) {
        setBulkResult(result.payload);
      }
    } catch (error) {
      console.error('Failed to disable all children:', error);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {association?.feature?.name} {t('features.settings', 'Settings')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Default for new children */}
          <Box>
            <FormControlLabel
              label={t('features.defaultForNewChildren', `Enable by default for new ${childEntityLabel}`)}
              control={
                <Switch checked={defaultEnabled} onChange={handleDefaultEnabledChange} disabled={disabled || loading} />
              }
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
              {t(
                'features.defaultForNewChildrenHelp',
                `When enabled, newly created ${childEntityLabel} will automatically have this feature enabled.`,
              )}
            </Typography>
          </Box>

          {/* Bulk Actions */}
          <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('features.bulkActions', 'Bulk Actions')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
              <Button variant="outlined" size="small" onClick={handleBulkEnable} disabled={disabled || loading}>
                {t('features.enableForAll', `Enable for all ${childEntityLabel}`)}
              </Button>
              <Button variant="outlined" size="small" onClick={handleBulkDisable} disabled={disabled || loading}>
                {t('features.disableForAll', `Disable for all ${childEntityLabel}`)}
              </Button>
            </Box>

            {bulkResult && (
              <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                {bulkResult.affected_count}{' '}
                {bulkResult.affected_count === 1 ? childEntityLabelSingular : childEntityLabel}{' '}
                {t('features.updated', 'updated')}
                {bulkResult.created_count > 0 && ` (${bulkResult.created_count} ${t('features.created', 'created')}`}
                {bulkResult.updated_count > 0 &&
                  `${bulkResult.created_count > 0 ? ', ' : ' ('}${bulkResult.updated_count} ${t('features.modified', 'modified')}`}
                {(bulkResult.created_count > 0 || bulkResult.updated_count > 0) && ')'}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default FeatureSettingsDialog;
